import React from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { track } from '@/lib/track';
import NewsletterSignup from '@/components/NewsletterSignup';

const navLinks = [
  { to: '/', label: 'Home' },
  { to: '/locations', label: 'Locations' },
  { to: '/how-it-works', label: 'How It Works' },
  { to: '/for-practitioners', label: 'For Practitioners' },
  { to: '/our-purpose', label: 'Our Purpose' },
  { to: '/book-a-viewing', label: 'Book a Viewing' },
];

export default function PublicLayout() {
  const [menuOpen, setMenuOpen] = React.useState(false);
  const location = useLocation();
  React.useEffect(() => { setMenuOpen(false); }, [location]);

  return (
    <div className="min-h-screen bg-[#FAF9F6] font-body">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white/90 backdrop-blur-lg border-b border-[#E5E2DC] z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2" data-testid="public-brand">
            <img src="/logo.webp" alt="Go Freestyle Studios" className="h-14 sm:h-[60px]" style={{filter:'contrast(1.15) brightness(0.95)'}} />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map(link => (
              <NavLink key={link.to} to={link.to} end={link.to === '/'}
                className={({ isActive }) => `px-4 py-2 rounded-full text-sm font-medium transition-colors ${isActive ? 'bg-[#3A4035] text-white' : 'text-[#5C5C55] hover:bg-[#F2F0EB]'}`}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}>
                {link.label}
              </NavLink>
            ))}
            <div className="w-px h-6 bg-[#E5E2DC] mx-2" />
            <Link to="/login" onClick={() => track('login_clicked')} className="px-5 py-2 rounded-full text-sm font-medium border border-[#E5E2DC] text-[#3A4035] hover:bg-[#F2F0EB] transition-colors" data-testid="nav-login">
              Login
            </Link>
          </nav>

          {/* Mobile */}
          <div className="lg:hidden flex items-center gap-3">
            <Link to="/login" onClick={() => track('login_clicked')} className="px-4 py-1.5 rounded-full text-xs font-medium border border-[#E5E2DC] text-[#3A4035]" data-testid="mobile-login">Login</Link>
            <button onClick={() => setMenuOpen(!menuOpen)} className="p-2" data-testid="mobile-menu-toggle">
              {menuOpen ? <X className="w-5 h-5 text-[#3A4035]" strokeWidth={1.5} /> : <Menu className="w-5 h-5 text-[#3A4035]" strokeWidth={1.5} />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="lg:hidden border-t border-[#E5E2DC] bg-white px-4 py-4 space-y-1">
            {navLinks.map(link => (
              <NavLink key={link.to} to={link.to} end={link.to === '/'}
                className={({ isActive }) => `block px-4 py-3 rounded-xl text-sm font-medium ${isActive ? 'bg-[#3A4035] text-white' : 'text-[#5C5C55] hover:bg-[#F2F0EB]'}`}>
                {link.label}
              </NavLink>
            ))}
          </div>
        )}
      </header>

      {/* Content */}
      <main className="pt-16">
        <Outlet />
      </main>

      {/* WhatsApp floating button */}
      <a href="https://wa.me/442039207045?text=Hi%2C%20I%27m%20interested%20in%20a%20studio%20at%20Go%20Freestyle."
        target="_blank" rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-[#25D366] rounded-full shadow-[0_4px_20px_rgba(0,0,0,0.15)] flex items-center justify-center hover:bg-[#20BD5A] active:scale-95 transition-all"
        data-testid="whatsapp-chat-button"
        aria-label="Chat on WhatsApp">
        <svg viewBox="0 0 24 24" className="w-7 h-7 text-white fill-current">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </a>

      {/* Newsletter signup */}
      <NewsletterSignup />

      {/* Footer */}
      <footer className="bg-[#3A4035] text-white mt-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-12 sm:py-16">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            <div>
              <p className="font-heading text-lg font-medium mb-4">Go Freestyle Studios</p>
              <p className="text-sm text-white/70 leading-relaxed">Private, professional studios for independent health, wellbeing and beauty practitioners.</p>
            </div>
            <div>
              <p className="text-xs tracking-[0.15em] uppercase font-semibold text-white/55 mb-4">Explore</p>
              <div className="space-y-2">
                {navLinks.map(link => (
                  <Link key={link.to} to={link.to} className="block text-sm text-white/80 hover:text-white transition-colors">{link.label}</Link>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs tracking-[0.15em] uppercase font-semibold text-white/55 mb-4">Platform</p>
              <div className="space-y-2">
                <Link to="/login" className="block text-sm text-white/80 hover:text-white transition-colors">Login</Link>
                <a href="mailto:hello@gofreestyle.co.uk" className="block text-sm text-white/80 hover:text-white transition-colors">Get in Touch</a>
                <Link to="/privacy" className="block text-sm text-white/80 hover:text-white transition-colors">Privacy Policy</Link>
              </div>
            </div>
            <div>
              <p className="text-xs tracking-[0.15em] uppercase font-semibold text-white/55 mb-4">Contact</p>
              <div className="space-y-1.5">
                <p className="text-sm text-white/80">0203 920 7045</p>
                <p className="text-sm text-white/80">hello@gofreestyle.co.uk</p>
                <a href="https://wa.me/442039207045" target="_blank" rel="noopener noreferrer" className="block text-sm text-white/80 hover:text-white transition-colors">WhatsApp</a>
              </div>
            </div>
          </div>
          <div className="border-t border-white/15 pt-8">
            <p className="text-sm tracking-[0.06em] text-white/60 text-center font-normal">One neighbourhood. One business. One studio at a time.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
